import React, { useState, useEffect } from 'react';
import { memberAPI } from '../services/api';
import './PaceOfPlaySection.css';

function PaceOfPlaySection({ member, onPaceDataUpdated }) {
  const [paceData, setPaceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (member?._id) {
      loadPaceData();
    }
  }, [member]);

  const loadPaceData = async () => {
    if (!member?._id) return;

    try {
      setLoading(true);
      setError(null);
      const response = await memberAPI.getMemberPaceStats(member._id);
      
      // Extract the actual pace stats from the response
      if (response.data && response.data.data) {
        const { paceStats } = response.data.data;
        setPaceData(paceStats);
      } else {
        setPaceData(null);
      }
    } catch (err) {
      console.error('Error loading pace data:', err);
      setError('Failed to load pace data');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncPaceData = async () => {
    if (!member?._id) return;

    // Check if member has GHIN
    if (!member.ghin) {
      setError('Member GHIN is required for pace data sync. Please add a GHIN number to this member.');
      return;
    }

    try {
      setSyncing(true);
      setError(null);
      const response = await memberAPI.syncMemberPaceData(member._id);
      
      if (!response.data || !response.data.success) {
        throw new Error('Invalid API response format');
      }
      
      const result = response.data.data;
      
      // Reload pace data to show updates
      await loadPaceData();
      
      // Notify parent component
      if (onPaceDataUpdated) {
        onPaceDataUpdated(result);
      }

      alert(`Pace data sync completed!\nFound ${result.recordsFound || 0} records for GHIN ${member.ghin}.`);
    } catch (err) {
      console.error('Error syncing pace data:', err);
      setError(err.response?.data?.error || err.message || 'Failed to sync pace data');
    } finally {
      setSyncing(false);
    }
  };

  const formatTime = (minutes) => {
    if (!minutes) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getPaceRatingColor = (rating) => {
    switch (rating) {
      case 'fast': return '#28a745';
      case 'average': return '#ffc107';
      case 'slow': return '#fd7e14';
      case 'very-slow': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getPaceRatingLabel = (rating) => {
    switch (rating) {
      case 'fast': return 'Fast';
      case 'average': return 'Average';
      case 'slow': return 'Slow';
      case 'very-slow': return 'Very Slow';
      default: return 'Unknown';
    }
  };

  if (loading && !paceData) {
    return (
      <div className="pace-section">
        <h3>Pace of Play</h3>
        <div className="pace-loading">
          <div className="pace-spinner"></div>
          <p>Loading pace data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pace-section">
      <div className="pace-header">
        <h3>Pace of Play</h3>
        <button 
          className="btn btn-small btn-secondary pace-sync-btn"
          onClick={handleSyncPaceData}
          disabled={syncing || !member.ghin}
          title={!member.ghin ? 'Member GHIN is required for pace data sync' : 'Sync pace data from Balboa Park system'}
        >
          {syncing ? 'Syncing...' : 'Sync Data'}
        </button>
      </div>

      {error && (
        <div className="pace-error">
          <p>{error}</p>
        </div>
      )}

      {paceData ? (
        <div className="pace-content">
          {paceData.syncStatus !== 'never' ? (
            <>
              {/* Summary Statistics */}
              <div className="pace-summary">
                <div className="pace-stat-grid">
                  <div className="pace-stat">
                    <span className="stat-value">{paceData.totalRounds || 0}</span>
                    <span className="stat-label">Total Rounds</span>
                  </div>
                  <div className="pace-stat">
                    <span className="stat-value">{formatTime(paceData.averageElapsedTime)}</span>
                    <span className="stat-label">Avg Time</span>
                  </div>
                  <div className="pace-stat">
                    <span className="stat-value">{formatTime(paceData.averageBehindGroup)}</span>
                    <span className="stat-label">Avg Behind</span>
                  </div>
                  <div className="pace-stat">
                    <span 
                      className="stat-value pace-rating"
                      style={{ color: getPaceRatingColor(paceData.paceRating) }}
                    >
                      {getPaceRatingLabel(paceData.paceRating)}
                    </span>
                    <span className="stat-label">Rating</span>
                  </div>
                </div>

                {paceData.fastestRound && paceData.slowestRound && (
                  <div className="pace-range">
                    <span>Range: {formatTime(paceData.fastestRound)} - {formatTime(paceData.slowestRound)}</span>
                  </div>
                )}
              </div>

              {/* Sync Information */}
              <div className="pace-sync-info">
                <div className="sync-status">
                  <span className={`sync-badge ${paceData.syncStatus}`}>
                    {paceData.syncStatus === 'success' ? '✓' : 
                     paceData.syncStatus === 'error' ? '✗' : 
                     paceData.syncStatus === 'syncing' ? '⏳' : '?'}
                  </span>
                  <span>Last sync: {formatDate(paceData.lastSyncDate)}</span>
                </div>
                {paceData.externalMemberId && (
                  <div className="external-id">
                    External ID: {paceData.externalMemberId}
                  </div>
                )}
              </div>

              {/* Recent Rounds History */}
              {paceData.recentRounds && paceData.recentRounds.length > 0 && (
                <div className="pace-history">
                  <div className="history-header">
                    <button 
                      className="btn btn-link"
                      onClick={() => setShowHistory(!showHistory)}
                    >
                      {showHistory ? 'Hide' : 'Show'} Recent Rounds ({paceData.recentRounds.length})
                    </button>
                  </div>
                  
                  {showHistory && (
                    <div className="history-list">
                      {paceData.recentRounds.map((round, index) => (
                        <div key={index} className="history-item">
                          <div className="round-info">
                            <strong>{round.eventTitle || 'Golf Round'}</strong>
                            <span className="round-date">{formatDate(round.eventDate)}</span>
                          </div>
                          <div className="round-stats">
                            <span>Time: {formatTime(round.popElapsed)}</span>
                            {round.popBehindGroup > 0 && (
                              <span>Behind: {formatTime(round.popBehindGroup)}</span>
                            )}
                          </div>
                          {round.courseLabel && (
                            <div className="round-course">{round.courseLabel}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="pace-no-data">
              <p>No pace data available for this member.</p>
              <p className="pace-help-text">
                {member.ghin ? 
                  'Click "Sync Data" to fetch pace of play information from the Balboa Park system.' :
                  'Member must have a GHIN number to sync pace data. Please add a GHIN number first.'
                }
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="pace-no-data">
          <p>Unable to load pace data.</p>
        </div>
      )}
    </div>
  );
}

export default PaceOfPlaySection;